import React, { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import DownloadApp from '@/components/shared/DownloadApp';
import { 
  ShoppingCart,
  Calendar,
  Users,
  Package,
  FileText,
  CreditCard,
  Bell,
  Smartphone,
  Clock,
  CheckCircle,
  ArrowRight,
  Star,
  Zap,
  TrendingUp,
  Shield,
  BarChart3,
  Wrench,
  Car,
  Truck,
  Bike,
  DollarSign,
  Search,
  Hammer,
  Recycle,
  Camera,
  MapPin,
  Archive,
  Gavel,
  AlertCircle,
  ClipboardList,
  Settings,
  Database,
  Filter
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faMobile, faUser, faStickyNote, faBuilding } from '@fortawesome/free-solid-svg-icons';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
}

interface SolutionOption {
  title: string;
  description: string;
  features: string[];
  cta: string;
  ctaLink: string;
  highlighted?: boolean;
}

const ScrapYardSoftwarePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    interest: 'whitelabel',
    subject: '',
    message: ''
  });

  const features: Feature[] = [
    {
      icon: <ShoppingCart className="w-12 h-12" />,
      title: "eCommerce Parts Marketplace",
      description: "Sell used car, truck, and motorbike parts online 24/7",
      benefits: [
        "Multi-category parts catalog (cars, trucks, bikes)",
        "VIN-based parts search & compatibility",
        "Advanced search by make, model, year",
        "Real-time inventory availability",
        "High-quality part photos & descriptions",
        "Condition grading system (A, B, C grade)",
        "Customer reviews & ratings",
        "Secure online checkout"
      ]
    },
    {
      icon: <Gavel className="w-12 h-12" />,
      title: "Online Bidding System",
      description: "Let customers bid on rare and high-value parts",
      benefits: [
        "Live auction functionality",
        "Timed bidding with countdown",
        "Auto-bid & proxy bidding",
        "Real-time bid notifications",
        "Reserve price management",
        "Bid history tracking",
        "Winner notifications",
        "Auction analytics & reports"
      ]
    },
    {
      icon: <Recycle className="w-12 h-12" />,
      title: "Vehicle Dismantling Management",
      description: "Track vehicles from arrival to complete dismantling",
      benefits: [
        "Vehicle intake & registration",
        "VIN decoding & cataloging",
        "Dismantling workflow tracking",
        "Parts extraction scheduling",
        "Photo documentation per part",
        "Labor time tracking",
        "Scrap metal weight tracking",
        "Environmental compliance reporting"
      ]
    },
    {
      icon: <Database className="w-12 h-12" />,
      title: "Comprehensive Inventory System",
      description: "Manage thousands of parts across multiple locations",
      benefits: [
        "Multi-location yard management",
        "Shelf/bay/row location tracking",
        "Barcode & QR code scanning",
        "Part condition tracking",
        "Aging & obsolescence alerts",
        "Stock level synchronization",
        "Inter-yard transfer management",
        "Warranty period tracking"
      ]
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Customer Relationship Management",
      description: "Build lasting relationships with mechanics and DIY customers",
      benefits: [
        "Customer database with purchase history",
        "Vehicle fleet tracking for businesses",
        "Trade/retail pricing tiers",
        "Quote & inquiry management",
        "Parts request notifications",
        "Loyalty rewards program",
        "Automated follow-ups",
        "Customer credit management"
      ]
    },
    {
      icon: <Bell className="w-12 h-12" />,
      title: "Real-Time Bidding Notifications",
      description: "Keep customers informed throughout the bidding process",
      benefits: [
        "Push notifications for new bids",
        "Outbid alerts via email & SMS",
        "Auction ending reminders",
        "Winning bid confirmations",
        "Payment deadline alerts",
        "Collection/shipping updates",
        "New parts availability alerts",
        "Price drop notifications"
      ]
    },
    {
      icon: <Search className="w-12 h-12" />,
      title: "Advanced Parts Search & Finder",
      description: "Help customers find exact parts they need quickly",
      benefits: [
        "VIN lookup for compatibility",
        "Make/model/year filtering",
        "Part number cross-reference",
        "OEM & aftermarket matching",
        "Image-based search",
        "Vehicle-specific results",
        "Alternative parts suggestions",
        "Saved searches & alerts"
      ]
    },
    {
      icon: <Camera className="w-12 h-12" />,
      title: "Photo Documentation System",
      description: "Visual proof of part condition and quality",
      benefits: [
        "Multi-angle part photography",
        "Damage & wear documentation",
        "Before/after dismantling photos",
        "Video inspections for high-value items",
        "Photo watermarking",
        "Image compression & storage",
        "Gallery view for customers",
        "Photo-based quality grading"
      ]
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: "Quotes & Invoicing",
      description: "Professional quotations and invoices for all transactions",
      benefits: [
        "Instant quote generation",
        "Parts compatibility verification",
        "Core charge management",
        "Bulk pricing for quantities",
        "Tax calculation & compliance",
        "Trade discount automation",
        "Digital invoice delivery",
        "Payment terms management"
      ]
    },
    {
      icon: <CreditCard className="w-12 h-12" />,
      title: "Integrated Payment Processing",
      description: "Accept payments securely online and on-site",
      benefits: [
        "Credit/Debit card payments",
        "Mobile payment options",
        "Auction payment processing",
        "Deposit & final payment tracking",
        "Refund management",
        "Core deposit handling",
        "Payment plan options",
        "Multi-currency support"
      ]
    },
    {
      icon: <Truck className="w-12 h-12" />,
      title: "Shipping & Logistics",
      description: "Manage deliveries and collections efficiently",
      benefits: [
        "Shipping cost calculator",
        "Courier integration",
        "Local pickup scheduling",
        "Packaging requirements tracking",
        "Tracking number management",
        "Delivery confirmation",
        "Freight quotes for large parts",
        "Returns & exchanges handling"
      ]
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      title: "Analytics & Business Intelligence",
      description: "Data-driven insights to optimize your scrap yard",
      benefits: [
        "Revenue & profit tracking",
        "Best-selling parts analysis",
        "Dismantling efficiency metrics",
        "Auction performance reports",
        "Inventory turnover rates",
        "Customer acquisition analytics",
        "Yard capacity utilization",
        "Scrap metal value tracking"
      ]
    }
  ];

  const solutionOptions: SolutionOption[] = [
    {
      title: "Join CarSparesEzyFind",
      description: "List your scrap yard parts on our established marketplace",
      features: [
        "Access to thousands of part buyers",
        "Website + Android, iPhone & Huawei apps",
        "Online bidding & shopping system",
        "Integrated payment processing",
        "Marketing & SEO support",
        "No setup fees - Start immediately",
        "24/7 technical support",
        "Analytics & sales dashboard"
      ],
      cta: "Join the Marketplace",
      ctaLink: "https://www.CarSparesEzyFind.co.za/list-business"
    },
    {
      title: "WhiteLabel Solution",
      description: "Your own branded scrap yard management platform",
      features: [
        "Fully branded website & mobile apps",
        "Android, iPhone & Huawei native apps",
        "Complete scrap yard management system",
        "All features listed above included",
        "Your own domain & branding",
        "48-hour setup guarantee",
        "Unlimited parts & customers",
        "Dedicated account manager"
      ],
      cta: "Get Custom Quote",
      ctaLink: "#whitelabel-quote",
      highlighted: true
    }
  ];

  const biddingStages = [
    {
      stage: "Part Listed for Auction",
      icon: <Gavel className="w-6 h-6" />,
      notification: "New auction alert: '[Part Name]' now available for bidding"
    },
    {
      stage: "First Bid Received",
      icon: <DollarSign className="w-6 h-6" />,
      notification: "Push notification: 'Your auction has received its first bid!'"
    },
    {
      stage: "Outbid Alert",
      icon: <AlertCircle className="w-6 h-6" />,
      notification: "SMS & Email: 'You've been outbid on [Part Name] - Current bid: R1,500'"
    },
    {
      stage: "Counter Bid Placed",
      icon: <TrendingUp className="w-6 h-6" />,
      notification: "Real-time update: 'Your bid of R1,750 has been placed successfully'"
    },
    {
      stage: "Auction Ending Soon",
      icon: <Clock className="w-6 h-6" />,
      notification: "Push notification: 'Auction ends in 1 hour - Current leader'"
    },
    {
      stage: "Auction Won",
      icon: <CheckCircle className="w-6 h-6" />,
      notification: "Congratulations email: 'You won! Payment required within 48 hours'"
    },
    {
      stage: "Payment Received",
      icon: <CreditCard className="w-6 h-6" />,
      notification: "Payment confirmation with collection/shipping instructions"
    },
    {
      stage: "Ready for Collection",
      icon: <Bell className="w-6 h-6" />,
      notification: "SMS: 'Your part is ready for pickup at Bay 12, Row 5'"
    },
    {
      stage: "Collection Complete",
      icon: <Star className="w-6 h-6" />,
      notification: "Thank you message & request for review/rating"
    }
  ];

  const vehicleTypes = [
    { icon: <Car className="w-12 h-12" />, name: "Cars", count: "50,000+ Parts" },
    { icon: <Truck className="w-12 h-12" />, name: "Trucks", count: "15,000+ Parts" },
    { icon: <Bike className="w-12 h-12" />, name: "Motorbikes", count: "8,000+ Parts" }
  ];

  const stats = [
    { value: "1,200+", label: "Scrap Yards" },
    { value: "500K+", label: "Parts Listed" },
    { value: "100K+", label: "Monthly Auctions" },
    { value: "48hrs", label: "Setup Time" }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you within 24 hours with your custom quote.');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Head>
        <title>Scrap Yard Software - Parts eCommerce, Bidding & Management | CarSparesEzyFind</title>
        <meta name="description" content="Complete scrap yard management software with eCommerce parts sales, online bidding, vehicle dismantling, CRM, inventory management, and mobile apps for Android, iPhone & Huawei." />
        <meta name="keywords" content="scrap yard software, auto salvage software, parts bidding system, vehicle dismantling, used parts ecommerce, salvage yard management" />
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-amber-700 via-orange-700 to-red-800 text-white py-20 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Recycle className="w-4 h-4 mr-2 text-green-300" />
                <span className="text-sm font-semibold">Complete Scrap Yard & Auto Salvage Solution</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Digitize Your <span className="text-yellow-300">Scrap Yard Business</span>
              </h1>
              
              <p className="text-xl md:text-2xl mb-8 text-orange-100">
                eCommerce Parts Store • Online Bidding • Dismantling Tracking • Inventory • CRM • Payments
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <a 
                  href="https://www.CarSparesEzyFind.co.za/list-business" 
                  className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center"
                >
                  Join Our Platform
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a 
                  href="#whitelabel-quote" 
                  className="bg-white hover:bg-gray-100 text-orange-800 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center border-2 border-white"
                >
                  Get Your Own Platform
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                {stats.map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-3xl font-bold text-yellow-300">{stat.value}</div>
                    <div className="text-sm text-orange-100 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Vehicle Types Section */}
        <section className="py-12 bg-gradient-to-r from-green-600 to-teal-600 text-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold mb-2">Parts for All Vehicle Types</h3>
              <p className="text-green-100">Cars • Trucks • Motorbikes</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {vehicleTypes.map((type, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all">
                  <div className="text-yellow-300 flex justify-center mb-3">
                    {type.icon}
                  </div>
                  <h4 className="text-2xl font-bold mb-1">{type.name}</h4>
                  <p className="text-green-100">{type.count}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile Apps & Features Highlight */}
        <section className="py-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
              <div className="flex items-center gap-4">
                <Gavel className="w-16 h-16 text-yellow-300" />
                <div>
                  <h3 className="text-2xl font-bold mb-1">Online Bidding</h3>
                  <p className="text-blue-100">Live auctions for rare parts</p>
                </div>
              </div>
              <div className="hidden md:block w-px h-16 bg-white/30"></div>
              <div className="flex items-center gap-4">
                <ShoppingCart className="w-16 h-16 text-yellow-300" />
                <div>
                  <h3 className="text-2xl font-bold mb-1">eCommerce Store</h3>
                  <p className="text-blue-100">Sell parts 24/7 online</p>
                </div>
              </div>
              <div className="hidden md:block w-px h-16 bg-white/30"></div>
              <div className="flex items-center gap-4">
                <Smartphone className="w-16 h-16 text-yellow-300" />
                <div>
                  <h3 className="text-2xl font-bold mb-1">Mobile Apps</h3>
                  <p className="text-blue-100">Android, iPhone & Huawei</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Complete Scrap Yard Management System
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need to run a modern, efficient auto salvage business
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-8 hover:shadow-2xl transition-all hover:border-orange-600 transform hover:-translate-y-2"
                >
                  <div className="text-orange-600 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Dismantling Workflow Section */}
        <section className="py-20 bg-gradient-to-br from-slate-900 to-orange-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Recycle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Vehicle Dismantling Management
                </h2>
                <p className="text-xl text-orange-200">
                  Track every vehicle from arrival to complete dismantling
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: <Car className="w-8 h-8" />, title: "Vehicle Intake", desc: "Register & VIN decode incoming vehicles" },
                  { icon: <Camera className="w-8 h-8" />, title: "Photo Documentation", desc: "Before & during dismantling photos" },
                  { icon: <Hammer className="w-8 h-8" />, title: "Parts Extraction", desc: "Track removal of saleable parts" },
                  { icon: <Package className="w-8 h-8" />, title: "Inventory Addition", desc: "Auto-catalog extracted parts" },
                  { icon: <ClipboardList className="w-8 h-8" />, title: "Quality Grading", desc: "Grade parts by condition (A/B/C)" },
                  { icon: <MapPin className="w-8 h-8" />, title: "Location Tracking", desc: "Assign shelf/bay locations" },
                  { icon: <Archive className="w-8 h-8" />, title: "Scrap Processing", desc: "Track remaining scrap metal weight" },
                  { icon: <BarChart3 className="w-8 h-8" />, title: "ROI Analytics", desc: "Vehicle profitability reports" }
                ].map((item, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all">
                    <div className="text-green-400 flex justify-center mb-3">
                      {item.icon}
                    </div>
                    <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                    <p className="text-orange-200 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Bidding Process Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Gavel className="w-16 h-16 text-orange-600 mx-auto mb-4" />
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Real-Time Bidding Notifications
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Keep customers engaged throughout the entire auction process
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-500 to-red-600 hidden md:block"></div>

                {biddingStages.map((stage, index) => (
                  <div key={index} className="relative mb-8 md:ml-20">
                    <div className="absolute -left-20 top-2 hidden md:block">
                      <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center text-white shadow-lg">
                        {stage.icon}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-orange-600 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="md:hidden text-orange-600">{stage.icon}</div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-900 mb-2">{stage.stage}</h4>
                          <div className="flex items-start gap-2">
                            <Bell className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-600">{stage.notification}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Advanced Search Features */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Search className="w-16 h-16 text-orange-600 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  Advanced Parts Search & Finder
                </h2>
                <p className="text-xl text-gray-600">
                  Help customers find the exact parts they need instantly
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { icon: <Search className="w-8 h-8" />, title: "VIN Lookup", desc: "Decode VIN for exact compatibility" },
                  { icon: <Filter className="w-8 h-8" />, title: "Make/Model/Year", desc: "Filter by vehicle specifications" },
                  { icon: <Package className="w-8 h-8" />, title: "Part Number", desc: "Search by OEM or aftermarket numbers" },
                  { icon: <Camera className="w-8 h-8" />, title: "Image Search", desc: "Upload photo to find similar parts" },
                  { icon: <Car className="w-8 h-8" />, title: "Vehicle Specific", desc: "Only show compatible parts" },
                  { icon: <Star className="w-8 h-8" />, title: "Condition Filter", desc: "Filter by grade A, B, or C" }
                ].map((item, index) => (
                  <div key={index} className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border-2 border-orange-200 hover:shadow-lg transition-all">
                    <div className="text-orange-600 mb-3">
                      {item.icon}
                    </div>
                    <h4 className="font-bold text-lg mb-2 text-gray-900">{item.title}</h4>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Solution Options Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Choose Your Solution
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join our established platform or get your own branded solution
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {solutionOptions.map((option, index) => (
                <div 
                  key={index}
                  className={`relative rounded-2xl p-8 ${
                    option.highlighted 
                      ? 'bg-gradient-to-br from-orange-600 to-red-700 text-white shadow-2xl transform scale-105' 
                      : 'bg-white border-2 border-gray-200'
                  }`}
                >
                  {option.highlighted && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                        RECOMMENDED
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className={`text-3xl font-bold mb-2 ${option.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {option.title}
                    </h3>
                    <p className={option.highlighted ? 'text-orange-100' : 'text-gray-600'}>
                      {option.description}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                          option.highlighted ? 'text-yellow-300' : 'text-green-500'
                        }`} />
                        <span className={option.highlighted ? 'text-orange-50' : 'text-gray-700'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <a 
                    href={option.ctaLink}
                    className={`block text-center px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 ${
                      option.highlighted
                        ? 'bg-yellow-400 hover:bg-yellow-300 text-gray-900'
                        : 'bg-orange-600 hover:bg-orange-700 text-white'
                    }`}
                  >
                    {option.cta}
                    <ArrowRight className="inline-block ml-2 w-5 h-5" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WhiteLabel Quote Section */}
        <section id="whitelabel-quote" className="py-20 bg-gradient-to-br from-gray-900 to-orange-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <Zap className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Get Your WhiteLabel Scrap Yard Platform
                </h2>
                <p className="text-xl text-orange-100">
                  Custom branded website & mobile apps - Ready in 48 hours!
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Fully branded website with your logo",
                      "Android, iPhone & Huawei mobile apps",
                      "Complete eCommerce parts store",
                      "Online bidding & auction system",
                      "Vehicle dismantling management",
                      "CRM & customer management",
                      "Multi-location inventory control",
                      "VIN lookup & compatibility checker",
                      "Photo documentation system",
                      "Invoicing & payment processing",
                      "Push notifications & email alerts",
                      "Analytics & reporting dashboard",
                      "Your own domain name",
                      "Unlimited parts & customers"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <ArrowRight className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <Clock className="w-6 h-6 text-blue-400 mr-2" />
                    Quick Setup Process
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="bg-orange-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</div>
                      <div>
                        <h4 className="font-bold mb-1">Submit Your Details</h4>
                        <p className="text-orange-100 text-sm">Fill out the form below with your scrap yard information</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-orange-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</div>
                      <div>
                        <h4 className="font-bold mb-1">Receive Custom Quote</h4>
                        <p className="text-orange-100 text-sm">Get a detailed proposal within 24 hours</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-orange-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</div>
                      <div>
                        <h4 className="font-bold mb-1">Launch Your Platform</h4>
                        <p className="text-orange-100 text-sm">Your branded solution goes live in 48 hours</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-yellow-400/20 border-2 border-yellow-400 rounded-lg">
                    <p className="font-bold text-yellow-300">
                      ⚡ Special Offer: First 10 scrap yards get 40% off!
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 md:p-12 text-gray-900">
                <h3 className="text-3xl font-bold text-center mb-8">Request Your Custom Quote</h3>
                
                <form onSubmit={handleSubmit}>
                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='name' 
                        placeholder='Enter name' 
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-orange-600 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faUser} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='flex flex-col md:flex-row md:gap-5'>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Phone *</label>
                      <div className='relative'>
                        <div className='flex'>
                          <input 
                            type='text' 
                            name='code' 
                            placeholder='27' 
                            value={'+27'} 
                            disabled 
                            className='form-control border border-gray-300 border-r-0 text-sm xl:text-[14px] 2xl:text-md h-10 px-3 w-[50px] rounded-l' 
                          />
                          <input 
                            type='text' 
                            name='phone' 
                            placeholder='Enter mobile number' 
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded-r focus:border-orange-600 focus:outline-none' 
                          />
                        </div>
                        <FontAwesomeIcon icon={faMobile} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Email *</label>
                      <div className='relative'>
                        <input 
                          type='email' 
                          name='email' 
                          placeholder='Enter email Id' 
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-orange-600 focus:outline-none' 
                        />
                        <FontAwesomeIcon icon={faEnvelope} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Scrap Yard/Business Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='businessName' 
                        placeholder='Enter your scrap yard name' 
                        value={formData.businessName}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-orange-600 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faBuilding} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>I'm interested in *</label>
                    <select
                      name='interest'
                      value={formData.interest}
                      onChange={handleChange}
                      className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-orange-600 focus:outline-none'
                    >
                      <option value="whitelabel">WhiteLabel Solution</option>
                      <option value="join">Joining CarSparesEzyFind</option>
                      <option value="both">Both Options - Need More Info</option>
                    </select>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Subject</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='subject' 
                        placeholder='Subject' 
                        value={formData.subject}
                        onChange={handleChange}
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-orange-600 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faStickyNote} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Message</label>
                    <textarea 
                      name='message'
                      placeholder='Tell us about your scrap yard and requirements' 
                      rows={5} 
                      value={formData.message}
                      onChange={handleChange}
                      className='form-control py-3 border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full px-3 rounded focus:border-orange-600 focus:outline-none'
                    />
                  </div>

                  <div className='text-right'>
                    <button 
                      type="submit"
                      className='bg-orange-600 text-white border border-orange-600 uppercase transition-all hover:bg-white hover:text-orange-600 px-10 py-3 rounded font-bold'
                    >
                      Get My Quote
                    </button>
                  </div>
                </form>

                <p className="text-center text-sm text-gray-600 mt-6">
                  🔒 Your information is secure. We'll contact you within 24 hours with a detailed quote.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-amber-700 to-red-800 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Digitize Your Scrap Yard?
            </h2>
            <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
              Join thousands of successful auto salvage businesses using our platform to increase sales and efficiency
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://www.CarSparesEzyFind.co.za/list-business"
                className="bg-white hover:bg-gray-100 text-orange-800 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Join CarSparesEzyFind
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a 
                href="#whitelabel-quote"
                className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Get WhiteLabel Solution
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </section>
      </div>
      <DownloadApp/>
    </>
  );
};

export default ScrapYardSoftwarePage;